// src/views/Payments/RefundListPage.js
import React, { useEffect, useMemo, useState } from "react";
import { Card, Container, Row, Col, Form, Button, Badge } from "react-bootstrap";
import { DataGrid } from "@mui/x-data-grid";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import refundApi from "../../services/refundApi";
import branchApi from "../../services/branchapi";
import moment from "moment";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function RefundListPage() {
  const navigate = useNavigate();
  const query = useQuery();

  const [orderId, setOrderId] = useState(query.get("q") || query.get("orderId") || "");
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0); // DataGrid: 0-based
  const [pageSize, setPageSize] = useState(10);

  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);

  const [fromDate, setFromDate] = useState(query.get("fromDate") || moment().format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState(query.get("toDate") || moment().format("YYYY-MM-DD"));

  const [roleId, setRoleId] = useState(null);
  const [userBranchId, setUserBranchId] = useState(null);

  const [allBranches, setAllBranches] = useState([]);
  const [branchId, setBranchId] = useState(query.get("branchId") || "");

  const fetchRefunds = async ({ p = page, ps = pageSize, oid = orderId } = {}) => {
    setLoading(true);
    try {
      const q = oid && String(oid).trim() ? String(oid).trim() : null;

      const effectiveBranchId =
        roleId === 1 ? (branchId ? Number(branchId) : null) : (userBranchId ? Number(userBranchId) : null);

      const res = await refundApi.listAllRefunds({
        page: p,
        limit: ps,
        q,
        branchId: effectiveBranchId,
        fromDate,
        toDate,
      });

      const result = res?.data?.data?.result || [];
      const total = res?.data?.data?.totalRecords || 0;

      setRows(
        result.map((r) => ({
          id: r.id,
          txnId: r.txnId,
          grOrderId: r.grOrderId,
          grOrderShowId: r.grOrderShowId,
          refundType: r.refundType,
          amountPaise: r.amountPaise,
          currency: r.currency,
          pgName: r.pgName,
          pgPaymentId: r.pgPaymentId,
          pgRefundId: r.pgRefundId,
          merchantRefundToken: r.merchantRefundToken,
          completedAt: r.completedAt,
          status: r.status,
          reason: r.reason,
          createdAt: r.createdAt,
          txnAmountPaise: r.txnAmountPaise,
          txnRefundedPaise: r.txnRefundedPaise,
          remainingPaise: r.remainingPaise,
          isFullyRefunded: r.isFullyRefunded,
        }))
      );

      setRowCount(total);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Failed to load refunds");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    try {
      const raw = localStorage.getItem("accessData") || localStorage.getItem("adminUser");
      const u = raw ? JSON.parse(raw) : {};

      const rId = Number(u.userRole || u.roleId || u.role_id || 0);
      const bId = Number(u.branchId || u.branch_id || 0) || null;

      setRoleId(rId);
      setUserBranchId(bId);

      if (rId !== 1 && bId) setBranchId(String(bId));
    } catch (e) {
      setRoleId(0);
      setUserBranchId(null);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const resp = await branchApi.getAllBranches({ status: 1 });
        const rows = resp?.data?.data || resp?.data || [];
        const list = Array.isArray(rows) ? rows : rows?.result || rows?.data || [];
        setAllBranches(list);
      } catch { }
    };
    load();
  }, []);



  useEffect(() => {
    fetchRefunds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  const columns = useMemo(
    () => [
      { field: "id", headerName: "Refund ID", width: 110 },
      { field: "txnId", headerName: "Txn ID", width: 100 },
      { field: "grOrderId", headerName: "Order ID", width: 120 },
      { field: "grOrderShowId", headerName: "Show ID", width: 150 },
      {
        field: "refundType",
        headerName: "Type",
        width: 120,
        renderCell: (p) => (
          <Badge bg={p.value === "FULL" ? "success" : "warning"}>{p.value}</Badge>
        ),
      },
      {
        field: "amountPaise",
        headerName: "Amount",
        width: 140,
        renderCell: (p) => {
          const rupees = Number(p.row.amountPaise || 0) / 100;
          return <span>₹ {rupees.toFixed(2)}</span>;
        },
      },
      { field: "currency", headerName: "Cur", width: 80 },
      { field: "pgName", headerName: "PG", width: 110 },
      { field: "pgPaymentId", headerName: "Payment ID", width: 220 },
      { field: "pgRefundId", headerName: "Refund ID", width: 220 },
      {
        field: "status",
        headerName: "Status",
        width: 140,
        renderCell: (p) => {
          const v = String(p.value || "").toUpperCase();
          const bg =
            v === "PROCESSED" || v === "SUCCESS"
              ? "success"
              : v === "FAILED"
                ? "danger"
                : "secondary";
          return <Badge bg={bg}>{v}</Badge>;
        },
      },
      {
        field: "remainingPaise",
        headerName: "Refund Balance",
        width: 160,
        renderCell: (p) => {
          const rupees = Number(p.value || 0) / 100;
          return <b>₹ {rupees.toFixed(2)}</b>;
        },
      },
      {
        field: "createdAt",
        headerName: "Created",
        width: 190,
        renderCell: (p) => (p.value ? new Date(p.value).toLocaleString() : "-"),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 450,
        sortable: false,
        renderCell: (p) => {
          const remaining = Number(p.row.remainingPaise || 0);
          const isFullyRefunded = Boolean(p.row.isFullyRefunded) || remaining <= 0;
          const refundStatus = String(
            p.row.status || ""
          ).toUpperCase();

          const canCheckStatus = !p.row.completedAt && (
            (String(p.row.pgName).toLowerCase() === "payu" &&
              Boolean(p.row.pgPaymentId) && Boolean(p.row.merchantRefundToken)) ||
            (["INITIATED", "PROCESSING", "QUEUED", "IN_PROGRESS", "REQUESTED"].includes(refundStatus) &&
              Boolean(p.row.pgRefundId))
          );

          return (
            <div className="d-flex gap-2">
              <Button
                size="sm"
                variant="outline-primary"
                onClick={() => navigate(`/admin/orders/details/${p.row.grOrderId}`)}
              >
                View Order
              </Button>

              {canCheckStatus && (
                <Button
                  size="sm"
                  variant="outline-warning"
                  disabled={loading}
                  onClick={async () => {
                    try {
                      setLoading(true);

                      const response =
                        await refundApi
                          .reconcileRefund(
                            p.row.id
                          );

                      if (
                        response?.data
                          ?.status !==
                        "SUCCESS"
                      ) {
                        throw new Error(
                          response?.data
                            ?.message ||
                          "Unable to update refund status"
                        );
                      }

                      toast.success(
                        `Refund status: ${response?.data?.data?.status || "updated"}`
                      );

                      await fetchRefunds({
                        p: page,
                        ps: pageSize,
                        oid: orderId,
                      });
                    } catch (error) {
                      toast.error(
                        error?.response
                          ?.data
                          ?.message ||
                        error.message ||
                        "Unable to update refund status"
                      );
                    } finally {
                      setLoading(false);
                    }
                  }}
                >
                  Recheck Status
                </Button>
              )}

              {isFullyRefunded ? (
                <Button size="sm" variant="outline-secondary" disabled>
                  Fully Refunded
                </Button>
              ) : canCheckStatus ? (
                <Button size="sm" variant="outline-secondary" disabled>
                  Recheck before refunding
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="outline-success"
                  onClick={() => navigate(`/admin/payments/refunds/create?orderId=${p.row.grOrderId}`)}
                >
                  New Refund
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [
      navigate,
      loading,
      page,
      pageSize,
      orderId,
      fromDate,
      toDate,
      roleId,
      branchId,
      userBranchId,
    ]
  );

  const onSearch = () => {
    const q = (orderId || "").trim();
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (fromDate) params.set("fromDate", fromDate);
    if (toDate) params.set("toDate", toDate);
    if (roleId === 1 && branchId) params.set("branchId", branchId);

    navigate(`/admin/payments/refunds${params.toString() ? `?${params.toString()}` : ""}`, { replace: true });

    setPage(0);
    fetchRefunds({ p: 0, ps: pageSize, oid: q });
  };

  const onClear = () => {
    setOrderId("");
    navigate(`/admin/payments/refunds`, { replace: true });
    setPage(0);
    fetchRefunds({ p: 0, ps: pageSize, oid: "" });
  };

  return (
    <Container fluid>
      <ToastContainer />
      <Row className="mb-3">
        <Col>
          <h4 className="mb-0">Refunds</h4>
          <small className="text-muted">Online payment refunds list</small>
        </Col>
        <Col className="d-flex justify-content-end align-items-center">
          <Button variant="success" onClick={() => navigate("/admin/payments/refunds/create")}>
            + Create Refund
          </Button>
        </Col>
      </Row>



      <Card className="mb-3">
        <Card.Body>
          <Row className="align-items-end g-2">
            <Col md={3}>
              <Form.Label className="mb-1">From</Form.Label>
              <Form.Control type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} max={toDate} />
            </Col>

            <Col md={3}>
              <Form.Label className="mb-1">To</Form.Label>
              <Form.Control type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} min={fromDate} />
            </Col>

            <Col md={3}>
              <Form.Label className="mb-1">Branch</Form.Label>
              <Form.Select value={branchId} disabled={roleId !== 1} onChange={(e) => setBranchId(e.target.value)}>
                <option value="">All Branches</option>
                {allBranches?.map((b) => (
                  <option key={b.branchId || b.id} value={b.branchId || b.id}>
                    {b.branchName || b.name}
                  </option>
                ))}
              </Form.Select>
              {roleId !== 1 && <div className="text-muted" style={{ fontSize: 12 }}>Branch locked by your role</div>}
            </Col>

            <Col md={3}>
              <Form.Label className="mb-1">Order ID / Show ID</Form.Label>
              <Form.Control
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
                placeholder="346187 or 1769499222"
              />
            </Col>

            <Col md="auto">
              <Button variant="primary" onClick={onSearch}>Search</Button>
            </Col>

            <Col md="auto">
              <Button variant="outline-secondary" onClick={onClear}>Clear</Button>
            </Col>
          </Row>

        </Card.Body>
      </Card>

      <Card>
        <Card.Body style={{ height: 560 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            rowCount={rowCount}
            paginationMode="server"
            page={page}
            pageSize={pageSize}
            onPageChange={(p) => setPage(p)}
            onPageSizeChange={(ps) => {
              setPageSize(ps);
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
            disableSelectionOnClick
          />
        </Card.Body>
      </Card>
    </Container>
  );
}

